<template>
  <div class="flex flex-col items-center justify-center p-4">
    <!-- Dropzone -->
    <div v-if="pages.length === 0"  
      @click="fileInput.click()"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      class="w-full max-w-5xl transition-all duration-500 cursor-pointer rounded-3xl flex flex-col items-center justify-center text-center relative overflow-hidden group"
      :class="[isDragging ? 'scale-[1.01]' : '']">
      
      <!-- Animated gradient border -->
      <div class="absolute inset-0 rounded-3xl p-[1.5px] overflow-hidden">
        <div class="absolute inset-0 rounded-3xl transition-opacity duration-500"
          :class="isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
          style="background: conic-gradient(from 0deg, #D01020, #104060, #D01020);"></div>
        <div class="absolute inset-0 rounded-3xl transition-opacity duration-500"
          :class="isDragging ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'"
          style="background: #E0E0E0;"></div>
      </div>

      <!-- Inner card -->
      <div class="relative w-full rounded-[22px] py-16 px-10 transition-all duration-500 flex flex-col items-center bg-white border border-gray-100 shadow-lg">
        
        <!-- Subtle decorative glow -->
        <div class="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-32 rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-700 blur-3xl pointer-events-none" style="background: radial-gradient(circle, #D01020 0%, transparent 70%);"></div>

        <!-- Loading indicator -->
        <div v-if="isProcessing" class="absolute inset-0 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center z-50 rounded-[22px]">
          <div class="relative w-16 h-16 mb-5">
            <div class="absolute inset-0 rounded-full border-[3px] border-red-50"></div>
            <div class="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#D01020] animate-spin"></div>
          </div>
          <p class="text-gray-800 font-bold text-lg">Procesando {{ totalPagesBeingProcessed }} páginas...</p>
          <p class="text-gray-400 text-sm mt-1">Esto puede tomar un momento</p>
        </div>

        <div class="relative z-10 flex flex-col items-center">
          <!-- PDF Icon -->
          <div class="w-16 h-16 mb-7 flex items-center justify-center transition-all duration-300">
            <img :src="'/icon.png'" alt="PDF Icon" class="w-full h-full object-contain" />
          </div>
          
          <!-- Heading -->
          <span class="text-2xl font-bold text-[#104060] mb-2">Sube tu archivo PDF</span>
          <p class="text-gray-400 text-sm mb-8 max-w-sm">Selecciona o arrastra tus archivos PDF para comenzar a eliminar páginas</p>

          <!-- Upload Button -->
          <button
            @click.stop="fileInput.click()"
            class="text-white font-semibold px-10 py-3.5 rounded-xl active:scale-[0.97] transition-all duration-200 flex items-center gap-2.5 shadow-lg shadow-red-200/60 hover:shadow-xl hover:shadow-red-300/60 bg-[#D01020] hover:bg-[#B01010]">
            <Upload :size="18" />
            <span>Seleccionar Archivos PDF</span>
          </button>
          
          <!-- Divider -->
          <div class="mt-7 mb-1 flex items-center gap-3 w-full max-w-xs">
            <div class="h-px flex-1 bg-gray-200"></div>
            <kbd class="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-[10px] font-mono text-gray-500">CTRL + V</kbd>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-widest">o arrastra aquí</p>
            <div class="h-px flex-1 bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Trust Indicators (outside dropzone, hidden after upload) -->
    <div v-if="pages.length === 0" class="flex items-center justify-center gap-6 mt-6 w-full max-w-md mx-auto">
      <div class="flex items-center gap-1.5 text-gray-400">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
        <span class="text-[11px] font-medium">Encriptado</span>
      </div>
      <div class="flex items-center gap-1.5 text-gray-400">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
        </svg>
        <span class="text-[11px] font-medium">Auto-eliminado</span>
      </div>
      <div class="flex items-center gap-1.5 text-gray-400">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-[11px] font-medium">100% Gratis</span>
      </div>
    </div>

    <!-- Main Editor - Redesigned with modern card layout -->
    <div v-else class="bg-gray-50 rounded-2xl w-full max-w-7xl overflow-hidden shadow-xl border border-gray-200">
      <div class="max-w-7xl mx-auto">
        <!-- Toolbar - Sleek top bar -->
        <div class="bg-white px-6 py-4 border-b border-gray-100">
          <div class="flex flex-wrap items-center gap-3">
            <div class="flex items-center gap-3 mr-auto">
              <div class="w-10 h-10 rounded-xl bg-[#104060] flex items-center justify-center shadow-sm">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p class="text-gray-900 font-bold text-sm leading-tight" v-if="fileInfo.uniqueFiles.length">
                  {{ fileInfo.fileTextDisplay }}
                </p>
                <p class="text-gray-400 text-xs">{{ pages.length }} {{ fileInfo.pageText }}</p>
              </div>
            </div>

            <button @click="addMoreFiles" class="text-[#D01020] hover:text-[#B01010] px-3 py-2 text-sm font-semibold transition flex items-center gap-1.5 hover:bg-red-50 rounded-lg">
              <Plus :size="16"/>
              Agregar
            </button>
            <button @click="resetAll" class="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-2 rounded-xl flex gap-2 text-sm font-semibold transition items-center">
              <RotateCcw :size="15" />
              Reiniciar
            </button>
            <button @click="deleteSelected" class="bg-rose-50 hover:bg-rose-100 text-rose-500 px-4 py-2 rounded-xl font-semibold transition flex items-center gap-2 text-sm disabled:opacity-40" :disabled="selectedUids.length === 0">
              <Trash2 :size="16" />
              Eliminar
            </button>
            <button
              @click="saveAndDownload"
              :disabled="isDownloading"
              class="relative bg-[#D01020] hover:bg-[#B01010] disabled:opacity-60 disabled:cursor-not-allowed 
                    text-white px-5 py-2.5 rounded-xl flex gap-2 font-bold transition text-sm shadow-md shadow-red-100 items-center">
              <span v-if="isDownloading" class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v-4l-3.5 3.5L12 24v-4a8 8 0 01-8-8z">
                  </path>
                </svg>
                Procesando...
              </span>
              <span v-else class="flex items-center gap-2">
                <Download :size="16" />
                Descargar PDF
              </span>
            </button>
          </div>
        </div>

        <!-- Processing pages message -->
        <div v-if="isProcessing" class="mx-6 mt-4 bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-red-200 border-t-[#D01020]"></div>
          <p class="text-[#D01020] font-medium text-sm">Procesando {{ totalPagesBeingProcessed }} páginas... aparecerán en breve</p>
        </div>

        <div class="overflow-y-auto p-6" :style="pages.length > 15 ? `max-height: ${maxHeight}px` : ''">
          <!-- Pages Grid -->
          <draggable v-model="pages" item-key="uid" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5 mb-4">
            <template #item="{ element }">
              <div class="relative bg-white rounded-2xl overflow-hidden transition-all duration-200 cursor-move group shadow-sm hover:shadow-lg border border-gray-100"
                :class="[
                  selectedUids.includes(element.uid) ? 'ring-2 ring-[#D01020] ring-offset-2 border-red-300' : 'hover:border-red-200',
                  element.isLoading ? 'opacity-60' : ''
                ]"
                @click.stop="toggleSelect(element.uid)">
                
                <!-- Loading overlay -->
                <div v-if="element.isLoading" class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-20 rounded-2xl">
                  <div class="flex flex-col items-center gap-2">
                    <div class="animate-spin rounded-full h-7 w-7 border-2 border-red-50 border-t-[#D01020]"></div>
                  </div>
                </div>

                <!-- Thumbnail -->
                <div class="aspect-[3/4] overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img 
                    :src="element.thumbnail" 
                    class="max-w-full max-h-full p-1.5 object-contain transition-transform duration-300"
                    :style="{ transform: `rotate(${element.rotation}deg)` }"
                  />
                </div>

                <!-- Bottom info bar -->
                <div class="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span class="text-xs font-bold text-gray-500">Pág. {{ pages.indexOf(element) + 1 }}</span>
                  <span v-if="element.rotation !== 0" class="text-[10px] font-semibold text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
                    {{ element.rotation }}°
                  </span>
                </div>

                <!-- Selection indicator -->
                <div v-if="selectedUids.includes(element.uid)" class="absolute top-2 right-2 z-10">
                  <div class="bg-[#D01020] text-white rounded-lg w-6 h-6 flex items-center justify-center text-xs font-bold shadow-md">
                    ✓
                  </div>
                </div>

                <!-- Hover overlay -->
                <div class="absolute inset-0 bg-red-600/0 group-hover:bg-red-600/5 transition-all duration-200 rounded-2xl pointer-events-none"></div>
              </div>
            </template>
          </draggable>
        </div>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      multiple
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>

  <!-- Selection Action Bar - Floating pill design -->
  <transition name="slide-up">
    <div v-if="selectedUids.length > 0" class="flex items-center justify-between bg-white/95 backdrop-blur-md p-3 px-5 mt-6 rounded-2xl shadow-xl border border-gray-200 max-w-3xl mx-auto mb-8">
      <!-- Left Section -->
      <div class="flex items-center gap-3">
        <div class="w-8 h-8 bg-[#D01020] text-white rounded-xl text-sm font-bold flex items-center justify-center shadow-sm">
          {{ selectedUids.length }}
        </div>
        <span class="text-gray-700 text-sm font-semibold hidden sm:inline">
          {{ selectedUids.length === 1 ? 'página' : 'páginas' }}
        </span>
        <div class="h-5 w-px bg-gray-200 mx-1"></div>
        <button @click="selectAll" class="text-xs px-3 py-1.5 rounded-lg text-[#D01020] font-semibold hover:bg-red-50 transition"
          :disabled="selectedUids.length === pages.length">
          Todas
        </button>
        <button @click="deselectAll" class="text-xs px-3 py-1.5 rounded-lg text-gray-500 font-semibold hover:bg-gray-100 transition"
          :disabled="selectedUids.length === 0">
          Ninguna
        </button>
      </div>
      <!-- Right Section: Action Icons -->
      <div class="flex items-center gap-1.5"> 
        <!-- Preview -->
        <button
          @click="previewSelected"
          class="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:text-[#D01020] hover:bg-red-50 transition disabled:opacity-30"
          :disabled="selectedUids.length === 0"
          title="Vista previa">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/></svg>
        </button>
        <!-- Rotate Left -->
        <button
          @click="rotateSelected(-90)"
          class="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:text-[#D01020] hover:bg-red-50 transition disabled:opacity-30"
          :disabled="selectedUids.length === 0"
          title="Girar izquierda">
          <RotateCcw :size="18" />
        </button>
        <!-- Rotate Right -->
        <button
          @click="rotateSelected(90)"
          class="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:text-[#D01020] hover:bg-red-50 transition disabled:opacity-30"
          :disabled="selectedUids.length === 0"
          title="Girar derecha">
          <RotateCw :size="18" />
        </button>
        <div class="h-5 w-px bg-gray-200 mx-1"></div>
        <!-- Delete Button -->
        <button @click="deleteSelected"
          class="w-10 h-10 flex items-center justify-center bg-rose-50 hover:bg-rose-100 text-rose-500 rounded-xl transition disabled:opacity-30"
          :disabled="selectedUids.length === 0"
          title="Eliminar">
          <Trash2 :size="18" />
        </button>
      </div>
    </div>
  </transition>

  <!-- Fullscreen Preview Modal - Dark immersive style -->
  <div v-if="showPreviewModal" class="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
    <div class="w-full h-full flex flex-col overflow-hidden max-w-full max-h-full">

      <!-- Top Toolbar - Minimal -->
      <div class="bg-black/60 backdrop-blur-md px-6 py-3 flex items-center gap-2">
        <button @click="rotateCurrentPage(-90)" class="text-white/70 hover:text-white hover:bg-white/10 w-10 h-10 rounded-xl flex items-center justify-center transition">
          <RotateCcw :size="18" />
        </button>
        <button @click="rotateCurrentPage(90)" class="text-white/70 hover:text-white hover:bg-white/10 w-10 h-10 rounded-xl flex items-center justify-center transition">
          <RotateCw :size="18" />
        </button>
        <button @click="deleteCurrentPage" class="text-white/70 hover:text-rose-400 hover:bg-white/10 w-10 h-10 rounded-xl flex items-center justify-center transition">
          <Trash2 :size="18" />
        </button>
        <div class="flex-1"></div>
        <button @click="showPreviewModal = false" class="text-white/70 hover:text-white hover:bg-white/10 w-10 h-10 rounded-xl flex items-center justify-center transition">
          <X :size="22" />
        </button>
      </div>

      <!-- Swiper Preview -->
      <div class="flex-1 flex items-center justify-center overflow-auto p-4">
        <Swiper
          :key="swiperRerenderKey"
          :modules="[Navigation, Pagination]"
          navigation
          pagination
          space-between="20"
          slides-per-view="1"
          class="w-full h-full"
          @slideChange="onSlideChange"
          ref="swiperRef">
          <SwiperSlide
            v-for="(page, index) in previewPages"
            :key="page.uid + '-' + page.rotation">
            <div class="flex flex-col items-center justify-center w-full h-full">
              <img
                :key="page.uid + '-' + page.rotation + '-' + (page.thumbnail ? page.thumbnail.substring(0,50) : '')"
                :src="page.thumbnail"
                :alt="`Página ${page.pageNum}`"
                class="max-w-6xl h-[72vh] object-contain rounded-2xl shadow-2xl"
              />
              <div class="mt-3 text-white/60 text-sm font-medium bg-white/10 px-4 py-1.5 rounded-full backdrop-blur-sm">
                {{ index + 1 }} / {{ previewPages.length }}
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </div>
  </div>

  <!-- Full-Page Drop Overlay -->
  <teleport to="body">
    <transition name="page-drop">
      <div v-if="isPageDragging" 
        class="fixed inset-0 z-[9999] flex items-center justify-center"
        @dragover.prevent
        @drop.prevent="handlePageDrop">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-[#104060]/80 backdrop-blur-md"></div>
        <!-- Content -->
        <div class="relative z-10 flex flex-col items-center pointer-events-none">
          <div class="w-32 h-32 mb-8 rounded-3xl bg-white/15 backdrop-blur-sm border-2 border-dashed border-white/50 flex items-center justify-center">
            <svg class="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <h3 class="text-3xl font-bold text-white mb-3">Suelta tu PDF aquí</h3>
          <p class="text-lg text-white/70 font-medium">Arrastra y suelta en cualquier parte de la página</p>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import draggable from 'vuedraggable'
import { PDFDocument, degrees } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'
import { Upload, RotateCcw, RotateCw, Trash2, Download, X, Plus } from 'lucide-vue-next'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Pagination, Zoom } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/zoom'

const swiperRef = ref(null)
const swiperRerenderKey = ref(0)
const swiperActiveIndex = ref(0)
const isDownloading = ref(false)

const previewSelected = () => {
  previewPages.value = pages.value.filter(p => selectedUids.value.includes(p.uid))
  swiperActiveIndex.value = 0
  showPreviewModal.value = true
  nextTick(() => {
    swiperRef.value?.swiper?.slideTo(0)
  })
}

const onSlideChange = () => {
  swiperActiveIndex.value = swiperRef.value.swiper.activeIndex
}

const rotateCurrentPage = async (deg) => {
  const page = previewPages.value[swiperActiveIndex.value];
  if (!page) return;

  page.rotation = (page.rotation + deg + 360) % 360;

  const pdfjsDoc = pdfjsDocs.value[page.fileName];
  const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation);
  page.thumbnail = thumbnail;

  await nextTick();
  swiperRerenderKey.value++;

  await nextTick();
  if (swiperRef.value?.swiper) {
    try {
      swiperRef.value.swiper.slideTo(swiperActiveIndex.value, 0);
    } catch (e) {}
  }
};

const deleteCurrentPage = () => {
  const page = previewPages.value[swiperActiveIndex.value]
  if (!page) return

  pages.value = pages.value.filter(p => p.uid !== page.uid)
  selectedUids.value = selectedUids.value.filter(uid => uid !== page.uid)
  previewPages.value = previewPages.value.filter(p => p.uid !== page.uid)

  if (previewPages.value.length === 0) {
    showPreviewModal.value = false
  } else if (swiperActiveIndex.value >= previewPages.value.length) {
    swiperActiveIndex.value = previewPages.value.length - 1
    nextTick(() => {
      swiperRef.value?.swiper?.slideTo(swiperActiveIndex.value)
    })
  }
}

const workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).href
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc

const pages = ref([])
const selectedUids = ref([])
const pdfjsDocs = ref({})
const pdfLibDocs = ref({})
const previewPages = ref([])
const showPreviewModal = ref(false)
const previewCanvases = ref({})
const isDragging = ref(false)
const isPageDragging = ref(false)
const isProcessing = ref(false)
const totalPagesBeingProcessed = ref(0)
let uidCounter = 0
let pageDragCounter = 0
const fileInput = ref(null)
const currentPreviewIndex = ref(0)
const previewCanvas = ref(null)

const fileInfo = computed(() => {
  const uniqueFiles = [...new Set(pages.value.map(p => p.fileName))]
  const fileCount = uniqueFiles.length
  const pageText = pages.value.length === 1 ? 'página' : 'páginas'

  let fileTextDisplay = ''
  if (fileCount <= 2) {
    fileTextDisplay = uniqueFiles.join(', ')
  } else {
    fileTextDisplay = `${fileCount} archivos`
  }

  return { uniqueFiles, fileCount, fileTextDisplay, pageText }
})

const dropzoneOptions = {
  url: 'https://httpbin.org/post',
  autoProcessQueue: false,
  acceptedFiles: 'application/pdf',
  addRemoveLinks: false,
}

const handleDrop = (e) => {
  isDragging.value = false
  isPageDragging.value = false
  pageDragCounter = 0
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const handlePageDrop = (e) => {
  isPageDragging.value = false
  pageDragCounter = 0
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const handlePageDragEnter = (e) => {
  e.preventDefault()
  pageDragCounter++
  if (e.dataTransfer?.types?.includes('Files')) {
    isPageDragging.value = true
  }
}

const handlePageDragOver = (e) => {
  e.preventDefault()
}

const handlePageDragLeave = (e) => {
  e.preventDefault()
  pageDragCounter--
  if (pageDragCounter <= 0) {
    pageDragCounter = 0
    isPageDragging.value = false
  }
}

const handlePageDropGlobal = (e) => {
  e.preventDefault()
  isPageDragging.value = false
  pageDragCounter = 0
  const files = Array.from(e.dataTransfer.files)
  if (files.length) processFiles(files)
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  
  for (let item of items) {
    if (item.type === 'application/pdf' || item.type.startsWith('application/')) {
      const file = item.getAsFile()
      if (file && file.type === 'application/pdf') {
        processFiles([file])
        break
      }
    }
  }
}

const processFiles = async (files) => {
  const pdfFiles = files.filter(f => f.type === 'application/pdf')
  if (!pdfFiles.length) {
    alert('Por favor selecciona solo archivos PDF')
    return
  }

  isProcessing.value = true

  for (const file of pdfFiles) {
    try {
      const buffer = await file.arrayBuffer()

      const pdfLibDoc = await PDFDocument.load(buffer)
      pdfLibDocs.value[file.name] = pdfLibDoc

      const pdfjsDoc = await pdfjsLib.getDocument({ data: buffer }).promise
      pdfjsDocs.value[file.name] = pdfjsDoc

      totalPagesBeingProcessed.value = pdfjsDoc.numPages

      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        pages.value.push({
          uid: uidCounter++,
          fileName: file.name,
          pageNum: i,
          rotation: 0,
          thumbnail: '',
          isLoading: true
        })
      }

      let pageIndex = pages.value.length - pdfjsDoc.numPages
      for (let i = 1; i <= pdfjsDoc.numPages; i++) {
        const { thumbnail } = await renderPage(pdfjsDoc, i, 0)
        if (pages.value[pageIndex]) {
          pages.value[pageIndex].thumbnail = thumbnail
          pages.value[pageIndex].isLoading = false
        }
        pageIndex++
      }
    } catch (error) {
      console.error('Error processing PDF:', error)
      alert('Error al procesar el archivo PDF. Inténtalo de nuevo.')
    }
  }

  isProcessing.value = false
  totalPagesBeingProcessed.value = 0
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFiles(files)
  e.target.value = ''
}

const addMoreFiles = () => {
  fileInput.value.click()
}

const renderPage = async (pdfDoc, pageNum, rotation = 0) => {
  const page = await pdfDoc.getPage(pageNum);
  const viewport = page.getViewport({ scale: 2.5, rotation });
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { thumbnail: canvas.toDataURL("image/png") };
};

const toggleSelect = (uid) => {
  const idx = selectedUids.value.indexOf(uid)
  if (idx === -1) selectedUids.value.push(uid)
  else selectedUids.value.splice(idx, 1)
}

const selectAll = () => {
  selectedUids.value = pages.value.map(p => p.uid)
}

const deselectAll = () => {
  selectedUids.value = []
}

const rotateSelected = async (deg) => {
  try {
    const selectedPages = pages.value.filter(p => selectedUids.value.includes(p.uid))
    selectedPages.forEach(p => { p.isLoading = true })

    const renderPromises = selectedPages.map(async (page) => {
      try {
        const pdfjsDoc = pdfjsDocs.value[page.fileName]
        if (!pdfjsDoc) return
        page.rotation = (page.rotation + deg + 360) % 360
        const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
        page.thumbnail = thumbnail
      } catch (error) {
        console.error('Error rendering page:', error)
      } finally {
        page.isLoading = false
      }
    })

    await Promise.all(renderPromises)
  } catch (error) {
    console.error('Error in rotateSelected:', error)
    pages.value.forEach(p => {
      if (selectedUids.value.includes(p.uid)) p.isLoading = false
    })
  }
}

const deleteSelected = () => {
  pages.value = pages.value.filter(p => !selectedUids.value.includes(p.uid))
  selectedUids.value = []
}

const renderPreviewPage = async (index) => {
  if (!previewPages.value[index]) return
  await nextTick()
  const canvas = previewCanvas.value
  if (!canvas) return

  const page = previewPages.value[index]
  const pdfjsDoc = pdfjsDocs.value[page.fileName]
  if (!pdfjsDoc) return

  try {
    const pdfPage = await pdfjsDoc.getPage(page.pageNum)
    const container = canvas.parentElement
    const maxWidth = container.clientWidth * 0.9
    const maxHeight = container.clientHeight * 0.9
    let viewport = pdfPage.getViewport({ scale: 1, rotation: page.rotation })
    let scale = Math.min(maxWidth / viewport.width, maxHeight / viewport.height, 3)
    scale = Math.max(scale, 0.5)
    viewport = pdfPage.getViewport({ scale, rotation: page.rotation })
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    requestAnimationFrame(async () => {
      try {
        await pdfPage.render({ canvasContext: ctx, viewport }).promise
      } catch (err) {}
    })
  } catch (error) {
    console.error('Error rendering preview:', error)
  }
}

const previewNext = async () => {
  if (currentPreviewIndex.value < previewPages.value.length - 1) {
    currentPreviewIndex.value++
    await renderPreviewPage(currentPreviewIndex.value)
  }
}

const previewPrevious = async () => {
  if (currentPreviewIndex.value > 0) {
    currentPreviewIndex.value--
    await renderPreviewPage(currentPreviewIndex.value)
  }
}

const rotatePreviewPage = async (deg) => {
  const page = previewPages.value[currentPreviewIndex.value]
  if (!page) return
  try {
    page.isLoading = true
    page.rotation = (page.rotation + deg + 360) % 360
    const pdfjsDoc = pdfjsDocs.value[page.fileName]
    const { thumbnail } = await renderPage(pdfjsDoc, page.pageNum, page.rotation)
    page.thumbnail = thumbnail
    await renderPreviewPage(currentPreviewIndex.value)
  } catch (error) {
    console.error('Error rotating preview page:', error)
  } finally {
    page.isLoading = false
  }
}

const deletePreviewPage = () => {
  const page = previewPages.value[currentPreviewIndex.value]
  if (!page) return
  pages.value = pages.value.filter(p => p.uid !== page.uid)
  previewPages.value = previewPages.value.filter(p => p.uid !== page.uid)
  selectedUids.value = selectedUids.value.filter(uid => uid !== page.uid)
  if (currentPreviewIndex.value >= previewPages.value.length) {
    currentPreviewIndex.value = Math.max(0, previewPages.value.length - 1)
  }
  if (previewPages.value.length === 0) {
    showPreviewModal.value = false
  } else {
    renderPreviewPage(currentPreviewIndex.value)
  }
}

const createMergedPdf = async () => {
  const mergedPdf = await PDFDocument.create()
  for (const page of pages.value) {
    const origPdf = pdfLibDocs.value[page.fileName]
    const [copiedPage] = await mergedPdf.copyPages(origPdf, [page.pageNum - 1])
    if (page.rotation !== 0) {
      copiedPage.setRotation(degrees(page.rotation))
    }
    mergedPdf.addPage(copiedPage)
  }
  return await mergedPdf.save()
}

const saveAndDownload = async () => {
  try {
    isDownloading.value = true
    await nextTick()
    await new Promise(resolve => setTimeout(resolve, 50))

    const bytes = await createMergedPdf()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `eliminarpdf-${Date.now()}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
  } finally {
    isDownloading.value = false
  }
}

const resetAll = () => {
  pages.value = []
  selectedUids.value = []
  pdfjsDocs.value = {}
  pdfLibDocs.value = {}
  uidCounter = 0
}

const maxHeight = computed(() => {
  const itemHeight = 260
  const gap = 20
  const width = window.innerWidth
  let columns = 2
  if (width >= 640) columns = 3
  if (width >= 768) columns = 4
  if (width >= 1024) columns = 5
  if (width >= 1280) columns = 6
  const rowsFor15Items = Math.ceil(15 / columns)
  return rowsFor15Items * itemHeight + (rowsFor15Items - 1) * gap
})

onMounted(() => {
  window.addEventListener('paste', handlePaste)
  document.addEventListener('dragenter', handlePageDragEnter)
  document.addEventListener('dragover', handlePageDragOver)
  document.addEventListener('dragleave', handlePageDragLeave)
  document.addEventListener('drop', handlePageDropGlobal)
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
  document.removeEventListener('dragenter', handlePageDragEnter)
  document.removeEventListener('dragover', handlePageDragOver)
  document.removeEventListener('dragleave', handlePageDragLeave)
  document.removeEventListener('drop', handlePageDropGlobal)
})
</script>

<style scoped>
button:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  transform: none !important;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}

:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  color: white !important;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(8px);
  width: 44px;
  height: 44px;
  border-radius: 12px;
}

:deep(.swiper-button-next::after),
:deep(.swiper-button-prev::after) {
  font-size: 18px;
  font-weight: bold;
}

:deep(.swiper-pagination-bullet) {
  background: white !important;
  opacity: 0.3;
}

:deep(.swiper-pagination-bullet-active) {
  background: white !important;
  opacity: 1;
}

.page-drop-enter-active,
.page-drop-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-drop-enter-from,
.page-drop-leave-to {
  opacity: 0;
}

.page-drop-enter-from .relative {
  transform: scale(0.9);
}

.page-drop-enter-to .relative {
  transform: scale(1);
}
</style>
